import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { LayoutDashboard, Users, Calendar, Bell, LogOut, Search, Download, ChevronLeft, ChevronRight, Filter } from 'lucide-react';

const STAGES = ['new_inquiry','qualified_review','contacted','meeting_booked','discovery_completed','proposal','negotiation','won','lost','archived'];
const STAGE_LABELS = { new_inquiry:'New', qualified_review:'Review', contacted:'Contacted', meeting_booked:'Meeting', discovery_completed:'Discovery', proposal:'Proposal', negotiation:'Negotiation', won:'Won', lost:'Lost', archived:'Archived' };
const TIER_CLASSES = { A:'tier-a', B:'tier-b', C:'tier-c', D:'tier-d', E:'tier-e' };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [view, setView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filters, setFilters] = useState({ search: '', stage: '', tier: '', enterprise: '', skip: 0 });
  const [loading, setLoading] = useState(true);
  const adminName = localStorage.getItem('aurocle_admin_name') || 'Admin';

  const verify = useCallback(async () => {
    try { await api.adminVerify(); } catch { navigate('/admin'); }
  }, [navigate]);

  const loadStats = useCallback(async () => {
    try { const r = await api.getStats(); setStats(r.data); } catch {}
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      const params = { skip: filters.skip, limit: 20 };
      if (filters.search) params.search = filters.search;
      if (filters.stage) params.stage = filters.stage;
      if (filters.tier) params.tier = filters.tier;
      if (filters.enterprise === 'true') params.enterprise = true;
      const r = await api.getLeads(params);
      setLeads(r.data.leads); setTotal(r.data.total);
    } catch {}
  }, [filters]);

  const loadBookings = useCallback(async () => {
    try { const r = await api.getBookings({}); setBookings(r.data.bookings); } catch {}
  }, []);

  const loadNotifs = useCallback(async () => {
    try { const r = await api.getNotifications({}); setNotifs(r.data.notifications); setUnread(r.data.unread_count); } catch {}
  }, []);

  useEffect(() => { verify(); }, [verify]);
  useEffect(() => {
    setLoading(true);
    Promise.all([loadStats(), loadLeads(), loadBookings(), loadNotifs()]).finally(() => setLoading(false));
  }, [loadStats, loadLeads, loadBookings, loadNotifs]);

  const handleExport = async () => {
    try {
      const r = await api.exportCSV({});
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'aurocle_leads.csv'; a.click();
    } catch {}
  };

  const markAllRead = async () => {
    try { await api.markAllNotifsRead(); loadNotifs(); } catch {}
  };

  const logout = () => { localStorage.removeItem('aurocle_admin_token'); localStorage.removeItem('aurocle_admin_name'); navigate('/admin'); };

  const SIDEBAR = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'leads', icon: Users, label: 'Leads' },
    { id: 'bookings', icon: Calendar, label: 'Bookings' },
    { id: 'notifications', icon: Bell, label: 'Notifications', badge: unread },
  ];

  if (loading && !stats) return (
    <div className="min-h-screen bg-aurocle-base flex items-center justify-center">
      <div className="text-aurocle-gold text-sm font-mono animate-pulse">Loading Command Center...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-aurocle-base flex" data-testid="admin-dashboard">
      <aside className="w-56 bg-aurocle-surface border-r border-white/5 flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="p-5 border-b border-white/5">
          <div className="font-heading text-sm tracking-[0.12em] uppercase text-aurocle-text">Aurocle <span className="text-aurocle-gold">CMD</span></div>
          <div className="text-xs text-aurocle-text-secondary mt-1 font-mono">{adminName}</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {SIDEBAR.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.id} onClick={() => setView(item.id)} data-testid={`sidebar-${item.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-wider transition-all ${
                  view === item.id ? 'bg-aurocle-gold/10 text-aurocle-gold border border-aurocle-gold/20' : 'text-aurocle-text-secondary hover:text-aurocle-text border border-transparent'}`}>
                <Icon size={14} />
                <span className="flex-1 text-left">{item.label}</span>
                {item.badge > 0 && <span className="pill pill-gold text-[10px] px-1.5">{item.badge}</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/5">
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-wider text-aurocle-text-secondary hover:text-red-400 transition-colors" data-testid="admin-logout">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        {view === 'dashboard' && stats && (
          <div>
            <h1 className="font-heading text-xl text-aurocle-text mb-6">Operations Overview</h1>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Leads', value: stats.total_leads },
                { label: 'New Leads', value: stats.new_leads },
                { label: 'Enterprise', value: stats.enterprise_leads },
                { label: 'Meetings', value: stats.booked_meetings },
                { label: 'Urgent', value: stats.urgent_leads },
                { label: 'Awaiting', value: stats.awaiting_response },
                { label: 'Unread', value: stats.unread_notifications },
                { label: 'Callbacks', value: stats.pending_callbacks },
              ].map((s, i) => (
                <div key={i} className="p-4 bg-aurocle-surface border border-white/5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
                  <div className="text-xs font-mono text-aurocle-text-secondary uppercase tracking-wider">{s.label}</div>
                  <div className="text-2xl font-heading text-aurocle-text mt-1">{s.value}</div>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-aurocle-surface border border-white/5 p-5">
                <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-4">By Qualification Tier</h3>
                <div className="space-y-2">
                  {Object.entries(stats.by_tier || {}).map(([t, c]) => (
                    <div key={t} className="flex items-center justify-between">
                      <span className={`pill text-xs ${TIER_CLASSES[t]}`}>Tier {t}</span>
                      <span className="text-sm font-mono text-aurocle-text">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-aurocle-surface border border-white/5 p-5">
                <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-4">Recent Leads</h3>
                <div className="space-y-2">
                  {(stats.recent_leads || []).map((l) => (
                    <div key={l.id} className="flex items-center justify-between cursor-pointer hover:bg-white/2 p-2 -mx-2 transition-colors"
                      onClick={() => navigate(`/admin/leads/${l.id}`)}>
                      <div>
                        <div className="text-sm text-aurocle-text">{l.full_name}</div>
                        <div className="text-xs text-aurocle-text-secondary">{l.company_name}</div>
                      </div>
                      <span className={`pill text-[10px] ${TIER_CLASSES[l.qualification_tier]}`}>{l.qualification_tier}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {view === 'leads' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-xl text-aurocle-text">Leads ({total})</h1>
              <button onClick={handleExport} className="btn-outline text-xs py-2 px-4" data-testid="export-csv-btn">
                <Download size={12} /> Export CSV
              </button>
            </div>
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-aurocle-text-secondary" />
                <input type="text" placeholder="Search leads..." value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value, skip: 0})}
                  className="premium-input pl-9 text-xs" data-testid="lead-search-input" />
              </div>
              <select value={filters.stage} onChange={(e) => setFilters({...filters, stage: e.target.value, skip: 0})}
                className="premium-select text-xs w-40" data-testid="filter-stage">
                <option value="">All Stages</option>
                {STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
              </select>
              <select value={filters.tier} onChange={(e) => setFilters({...filters, tier: e.target.value, skip: 0})}
                className="premium-select text-xs w-32" data-testid="filter-tier">
                <option value="">All Tiers</option>
                {['A','B','C','D','E'].map(t => <option key={t} value={t}>Tier {t}</option>)}
              </select>
              <select value={filters.enterprise} onChange={(e) => setFilters({...filters, enterprise: e.target.value, skip: 0})}
                className="premium-select text-xs w-36" data-testid="filter-enterprise">
                <option value="">All Leads</option>
                <option value="true">Enterprise Only</option>
              </select>
              <button onClick={loadLeads} className="btn-outline text-xs py-2 px-4" data-testid="apply-filters-btn">
                <Filter size={12} /> Apply
              </button>
            </div>
            <div className="bg-aurocle-surface border border-white/5 overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Company</th><th>Country</th><th>Interest</th><th>Tier</th><th>Stage</th><th>Owner</th><th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="cursor-pointer" onClick={() => navigate(`/admin/leads/${l.id}`)} data-testid={`lead-row-${l.id}`}>
                      <td>
                        <div className="text-sm text-aurocle-text">{l.full_name}</div>
                        <div className="text-xs text-aurocle-text-secondary">{l.email}</div>
                      </td>
                      <td className="text-sm">{l.company_name}</td>
                      <td className="text-sm">{l.country}</td>
                      <td className="text-xs">{l.main_interest}</td>
                      <td><span className={`pill text-[10px] ${TIER_CLASSES[l.qualification_tier]}`}>{l.qualification_tier}</span></td>
                      <td><span className="pill text-[10px]">{STAGE_LABELS[l.pipeline_stage] || l.pipeline_stage}</span></td>
                      <td className="text-xs">{l.owner || '—'}</td>
                      <td className="text-xs font-mono">{l.created_at?.split('T')[0]}</td>
                    </tr>
                  ))}
                  {leads.length === 0 && <tr><td colSpan={8} className="text-center text-aurocle-text-secondary py-8">No leads found.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-aurocle-text-secondary font-mono">
                {filters.skip + 1}-{Math.min(filters.skip + 20, total)} of {total}
              </span>
              <div className="flex gap-2">
                <button disabled={filters.skip === 0} onClick={() => setFilters({...filters, skip: Math.max(0, filters.skip - 20)})}
                  className="btn-outline text-xs py-1.5 px-3 disabled:opacity-30"><ChevronLeft size={12} /></button>
                <button disabled={filters.skip + 20 >= total} onClick={() => setFilters({...filters, skip: filters.skip + 20})}
                  className="btn-outline text-xs py-1.5 px-3 disabled:opacity-30"><ChevronRight size={12} /></button>
              </div>
            </div>
          </div>
        )}

        {view === 'bookings' && (
          <div>
            <h1 className="font-heading text-xl text-aurocle-text mb-6">Bookings ({bookings.length})</h1>
            <div className="bg-aurocle-surface border border-white/5 overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Company</th><th>Type</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                      <td>
                        <div className="text-sm">{b.full_name}</div>
                        <div className="text-xs text-aurocle-text-secondary">{b.email}</div>
                      </td>
                      <td className="text-sm">{b.company_name}</td>
                      <td className="text-xs">{b.consultation_type}</td>
                      <td><span className={`pill text-[10px] ${b.meeting_status === 'scheduled' ? 'pill-gold' : b.meeting_status === 'completed' ? 'pill-success' : 'pill-warning'}`}>{b.meeting_status}</span></td>
                      <td className="text-xs font-mono">{b.created_at?.split('T')[0]}</td>
                    </tr>
                  ))}
                  {bookings.length === 0 && <tr><td colSpan={5} className="text-center text-aurocle-text-secondary py-8">No bookings yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'notifications' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-xl text-aurocle-text">Notifications</h1>
              {unread > 0 && <button onClick={markAllRead} className="btn-outline text-xs py-2 px-4" data-testid="mark-all-read-btn">Mark All Read</button>}
            </div>
            <div className="space-y-2">
              {notifs.map((n) => (
                <div key={n.id} className={`p-4 bg-aurocle-surface border transition-colors ${n.is_read ? 'border-white/5' : 'border-aurocle-gold/15'}`} data-testid={`notif-${n.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        {!n.is_read && <div className="w-1.5 h-1.5 rounded-full bg-aurocle-gold" />}
                        <span className="text-sm text-aurocle-text">{n.title}</span>
                        {n.priority === 'high' && <span className="pill pill-warning text-[9px]">HIGH</span>}
                      </div>
                      <p className="text-xs text-aurocle-text-secondary mt-1">{n.message}</p>
                    </div>
                    <span className="text-[10px] font-mono text-aurocle-text-secondary shrink-0">{n.created_at?.split('T')[0]}</span>
                  </div>
                </div>
              ))}
              {notifs.length === 0 && <div className="text-center text-aurocle-text-secondary py-12 text-sm">No notifications.</div>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
